import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";

import { useCompanies } from "../../../../hooks/companies";
import { useDateFilter } from "../../../../hooks/datefilter";
import { obterResumo } from "../../../../services/despachoIndicadoresApi";

// Resumo de despacho da aba "Despacho" da Torre (PO1-3494, épico PO1-3493).
//
// Os 6 componentes da aba leem o MESMO objeto de /despacho/indicadores/resumo.
// Em vez de 6 requests iguais, o hook busca UMA vez por recorte (embarcador +
// transportadora + período) e compartilha: cache em módulo por chave do recorte,
// com a promessa compartilhada enquanto a chamada está no ar (quem chega junto
// espera a mesma). A entrada do cache vive enquanto houver componente montado
// naquele recorte; quando o último sai, cai — a próxima montagem busca de novo
// (dado fresco; e o Meu Painel, que monta cada widget sozinho, continua
// buscando o próprio dado).
//
// Contrato de orquestração da Torre (`deferLoad` / `onRegisterFetch`), igual ao
// componentsPerformance/IndicadoresEntrega: com deferLoad, a PRIMEIRA carga fica
// com a fila da página; trocas de recorte depois disso carregam na hora. Sem
// props (Meu Painel) carrega ao montar.

const DIAS_PADRAO = 30;
const DATA = "YYYY-MM-DD";

const cache = new Map(); // chave -> { promessa, dados, assinantes }

// Para testes: zera o cache entre casos.
export function limparCacheResumo() {
  cache.clear();
}

export function periodoDespacho(startDate, endDate) {
  const inicio = startDate ? moment(startDate) : moment().subtract(DIAS_PADRAO, "days");
  const fim = endDate ? moment(endDate) : moment();
  return { de: inicio.format(DATA), ate: fim.format(DATA) };
}

export function chaveRecorte(empresaId, transportadoras, periodo) {
  const t = Array.isArray(transportadoras) ? transportadoras.join(",") : "";
  return `${empresaId === null || empresaId === undefined ? "todas" : empresaId}|${t}|${periodo.de}|${periodo.ate}`;
}

// Mesmo recorte e mesmo objeto de dados já no estado: devolve o estado atual
// para o React não re-renderizar (o efeito de carga pode rodar mais de uma vez
// por render quando o pai passa `onRegisterFetch` inline).
function mesmoEstado(atual, chave, dados) {
  return atual && atual.chave === chave && atual.dados === dados && !atual.carregando && !atual.erro ? atual : null;
}

function entradaDe(chave) {
  let entrada = cache.get(chave);
  if (!entrada) {
    entrada = { promessa: null, dados: null, assinantes: 0 };
    cache.set(chave, entrada);
  }
  return entrada;
}

// Devolve { dados, carregando, erro, periodo, recarregar }.
// `dados` é o resumo normalizado (services/despachoIndicadoresApi) ou null.
export default function useResumoDespacho({ deferLoad = false, onRegisterFetch } = {}) {
  const { empresa, transportadora, empresaId, arrayParams } = useCompanies();
  const { startDate, endDate } = useDateFilter();

  const periodo = useMemo(() => periodoDespacho(startDate, endDate), [startDate, endDate]);
  const transportadoras = arrayParams.transportadoras || null;
  const chave = chaveRecorte(empresaId, transportadoras, periodo);
  // Params derivados da chave (string estável), não do array do contexto, que
  // é um objeto novo a cada render.
  const params = useMemo(() => {
    const p = { ...periodo };
    if (transportadoras && transportadoras.length) p.transportadoras = transportadoras;
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  const [estado, setEstado] = useState(() => {
    const entrada = cache.get(chave);
    return { chave, dados: entrada && entrada.dados ? entrada.dados : null, carregando: false, erro: null };
  });

  const montado = useRef(true);
  useEffect(
    () => () => {
      montado.current = false;
    },
    []
  );

  // Assinatura do recorte: segura a entrada do cache enquanto este componente
  // estiver montado nele.
  useEffect(() => {
    const entrada = entradaDe(chave);
    entrada.assinantes += 1;
    return () => {
      entrada.assinantes -= 1;
      if (entrada.assinantes <= 0 && cache.get(chave) === entrada) cache.delete(chave);
    };
  }, [chave]);

  const carregar = useCallback(
    async (forcar = false) => {
      const entrada = entradaDe(chave);
      if (forcar) {
        entrada.dados = null;
        entrada.promessa = null;
      }
      if (entrada.dados) {
        if (montado.current) setEstado((atual) => mesmoEstado(atual, chave, entrada.dados) || { chave, dados: entrada.dados, carregando: false, erro: null });
        return entrada.dados;
      }
      if (!entrada.promessa) {
        entrada.promessa = obterResumo(params)
          .then((dados) => {
            entrada.dados = dados;
            return dados;
          })
          .catch((e) => {
            // Falhou: solta a promessa para a próxima tentativa buscar de novo.
            entrada.promessa = null;
            throw e;
          });
      }
      if (montado.current) setEstado((atual) => ({ ...atual, chave, carregando: true, erro: null }));
      try {
        const dados = await entrada.promessa;
        if (montado.current) setEstado((atual) => mesmoEstado(atual, chave, dados) || { chave, dados, carregando: false, erro: null });
        return dados;
      } catch (e) {
        if (montado.current) setEstado({ chave, dados: null, carregando: false, erro: e });
        return null;
      }
    },
    [chave, params]
  );

  // Primeira carga segue o contrato da Torre; depois disso toda troca de
  // recorte carrega na hora (a fila da página só roda ao trocar de aba).
  const jaCarregou = useRef(false);
  useEffect(() => {
    if (onRegisterFetch) {
      onRegisterFetch(() => {
        jaCarregou.current = true;
        return carregar();
      });
    }
    if (!deferLoad || jaCarregou.current) {
      jaCarregou.current = true;
      carregar();
    }
  }, [carregar, deferLoad, onRegisterFetch, empresa, transportadora]);

  const recarregar = useCallback(() => carregar(true), [carregar]);

  const dadosDoRecorte = estado.chave === chave ? estado.dados : null;
  return { dados: dadosDoRecorte, carregando: estado.carregando, erro: estado.erro, periodo, recarregar };
}
